import React, { useState, useEffect } from 'react';
import whatsapp from '../../assets/image/social/whatsapp.png';
import { motion, AnimatePresence } from 'framer-motion';
import { HashLink } from 'react-router-hash-link';
import { useLocation } from 'react-router-dom';

const Navbar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const toggleMenu = () => setMenuOpen(!menuOpen);
    const location = useLocation();
    const currentHash = location.hash || '#';

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const isActive = (to) => {
        const linkHash = to.replace('/', '');
        return currentHash === linkHash || (currentHash === '' && linkHash === '#');
    };

    // Animación del menú móvil
    const menuVariants = {
        hidden: { height: 0, opacity: 0, transition: { duration: 0.3, ease: "easeInOut" } },
        visible: {
            height: "auto",
            opacity: 1,
            transition: {
                duration: 0.4,
                ease: "easeInOut",
                staggerChildren: 0.07,
                delayChildren: 0.1
            }
        },
        exit: { height: 0, opacity: 0, transition: { duration: 0.3, ease: "easeInOut" } }
    };

    const itemVariants = {
        hidden: { x: -20, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.3, ease: "easeOut" } },
        exit: { x: -20, opacity: 0, transition: { duration: 0.2 } }
    };

    const mobileLinks = [
        { to: '/#home', label: 'INICIO' },
        { to: '/#about', label: 'ACERCA DE MI' },
        { to: '/#skills', label: 'HABILIDADES' },
        { to: '/#experience', label: 'RESUME' },
        { to: '/#portfolio', label: 'PORTAFOLIO' },
    ];

    return (
        <header 
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? 'bg-dark-900/80 backdrop-blur-lg border-b border-white/10 shadow-lg py-3' : 'bg-transparent py-5'
            }`}
        >
            <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
                {/* Logo */}
                <HashLink smooth to="/#home" className="text-2xl font-display font-bold text-white no-underline tracking-tighter">
                    Cesar<span className="text-accent-cyan">.</span>
                </HashLink>

                {/* Nav menu - Desktop */}
                <nav className="hidden md:flex items-center space-x-8">
                    {mobileLinks.map((link) => (
                        <HashLink
                            key={link.to}
                            smooth
                            to={link.to}
                            className={`text-sm font-medium tracking-wide transition-all duration-300 no-underline relative group ${
                                isActive(link.to) ? 'text-accent-cyan' : 'text-slate-300 hover:text-white'
                            }`}
                        >
                            {link.label}
                            <span 
                                className={`absolute -bottom-1 left-0 w-full h-[2px] bg-gradient-to-r from-accent-blue to-accent-cyan rounded-full transition-all duration-300 ${
                                    isActive(link.to) ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0 group-hover:opacity-100 group-hover:scale-x-100'
                                }`} 
                            />
                        </HashLink>
                    ))}
                </nav>

                {/* Contact & Mobile Menu */}
                <div className="flex items-center space-x-4">
                    <HashLink 
                        smooth 
                        to="https://wa.link/gzntrs" 
                        target="_blank"
                        className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-accent-cyan/50 hover:bg-white/5 transition-all duration-300 no-underline text-white font-medium text-sm"
                    >
                        <img src={whatsapp} width={20} alt="whatsapp" className="brightness-200" />
                        <span>Hablemos</span>
                    </HashLink>

                    {/* Mobile menu icon */}
                    <button 
                        onClick={toggleMenu} 
                        className="md:hidden text-white hover:text-accent-cyan focus:outline-none transition-colors p-2"
                    >
                        {menuOpen ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Menu with Framer Motion */}
            <AnimatePresence>
                {menuOpen && (
                    <motion.nav
                        key="mobile-menu"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={menuVariants}
                        className="md:hidden absolute top-full left-0 w-full overflow-hidden shadow-2xl z-40 bg-dark-800/95 backdrop-blur-xl border-b border-white/5"
                    >
                        <div className="flex flex-col py-4 px-6">
                            {mobileLinks.map((link, index) => (
                                <motion.div key={link.to} variants={itemVariants}>
                                    <HashLink
                                        smooth
                                        to={link.to}
                                        onClick={() => setMenuOpen(false)}
                                        className={`block w-full py-4 px-4 no-underline text-sm font-medium tracking-wider rounded-xl transition-all duration-300 ${
                                            isActive(link.to) 
                                                ? 'text-accent-cyan bg-accent-cyan/10' 
                                                : 'text-slate-300 hover:text-white hover:bg-white/5'
                                        }`}
                                    >
                                        <div className="flex items-center">
                                            <span className={`w-1.5 h-1.5 rounded-full mr-3 ${isActive(link.to) ? 'bg-accent-cyan shadow-[0_0_8px_rgba(34,211,238,0.8)]' : 'bg-slate-600'}`} />
                                            {link.label}
                                        </div>
                                    </HashLink>
                                </motion.div>
                            ))}
                        </div>

                        {/* WhatsApp mobile */}
                        <motion.div variants={itemVariants} className="px-6 pb-6 pt-2">
                            <HashLink 
                                to="https://wa.link/gzntrs" 
                                target="_blank"
                                onClick={() => setMenuOpen(false)}
                                className="flex items-center justify-center gap-3 w-full py-3 px-4 bg-gradient-to-r from-accent-blue/20 to-accent-purple/20 hover:from-accent-blue/30 hover:to-accent-purple/30 border border-white/10 rounded-xl no-underline text-white transition-all"
                            >
                                <img src={whatsapp} width={20} alt="whatsapp" className="brightness-200" />
                                <span className="font-medium text-sm">Contactar por WhatsApp</span>
                            </HashLink>
                        </motion.div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
